import React from 'react';
import { Box, Dialog, DialogContent, DialogContentText, DialogTitle, LinearProgress, Stack, Typography } from '@mui/material';

import type { BackupProgressState } from '../utils/backupExport';

type BackupProgressDialogProps = {
  progress: BackupProgressState;
  t: (key: string, variables?: Record<string, string | number>) => string;
};

const BackupProgressDialog: React.FC<BackupProgressDialogProps> = ({ progress, t }) => {
  const backupProgressTitle = t('BACKUP_PROGRESS_DIALOG_TITLE');
  const backupProgressStageLabel =
    progress.stage === 'preparing-data'
      ? t('BACKUP_PROGRESS_PREPARING_DATA')
      : progress.stage === 'collecting-books'
        ? t('BACKUP_PROGRESS_COLLECTING_BOOKS')
        : progress.stage === 'compressing'
          ? t('BACKUP_PROGRESS_COMPRESSING')
          : progress.stage === 'saving'
            ? t('BACKUP_PROGRESS_SAVING')
            : t('BACKUP_PROGRESS_FINALIZING');
  const backupProgressPercent = progress.percent ?? 0;

  return (
    <Dialog open={progress.open} aria-labelledby='backup-progress-dialog-title' disableEscapeKeyDown>
      <DialogTitle id='backup-progress-dialog-title'>{backupProgressTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', minWidth: 320, my: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>{t('BACKUP_IN_PROGRESS')}</DialogContentText>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            {backupProgressStageLabel}
          </Typography>
          <LinearProgress
            variant={progress.percent === null ? 'indeterminate' : 'determinate'}
            value={backupProgressPercent}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              {progress.total > 0
                ? t('BACKUP_PROGRESS_STEP_COUNT', {
                    current: progress.current,
                    total: progress.total,
                  })
                : ''}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {progress.percent === null
                ? ''
                : t('BACKUP_PROGRESS_PERCENT', {
                    percent: backupProgressPercent,
                  })}
            </Typography>
          </Stack>
          {progress.detail && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                display: 'block',
                mt: 2,
                fontStyle: 'italic',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {progress.detail}
            </Typography>
          )}
          {progress.filename && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                display: 'block',
                mt: progress.detail ? 1 : 2,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {progress.filename}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BackupProgressDialog;
